import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { getAppUpdateInfo } from '../services/appUpdates';
import { isNative } from './capacitor';
import { isRemoteVersionNewer } from './versionCompare';

/**
 * Must be called when the current bundle is ready and no OTA apply is pending.
 * On web this is a no-op.
 */
export async function notifyAppReady() {
  if (!isNative()) return;
  await CapacitorUpdater.notifyAppReady();
}

/** @returns {Promise<string>} */
export async function getInstalledVersion() {
  if (!isNative()) {
    return String(process.env.REACT_APP_VERSION || '').trim();
  }
  try {
    const info = await CapacitorUpdater.get();
    const v = info?.bundle?.version ?? info?.current?.version;
    if (v != null && String(v).trim() !== '') return String(v).trim();
  } catch (err) {
    console.warn('[v2-updater] CapacitorUpdater.get failed', err);
  }
  return String(process.env.REACT_APP_VERSION || '').trim();
}

/**
 * Fetch Pilot app_updates/customer-app and apply bundle when newer.
 * @param {(percent: number) => void} [onProgress]
 * @returns {Promise<boolean>} true when download+set was started (app will reload)
 */
export async function checkAndApplyOtaUpdate(onProgress) {
  if (!isNative()) return false;

  let remote;
  try {
    remote = await getAppUpdateInfo();
  } catch (err) {
    console.warn('[v2-updater] Failed to fetch app update info', err);
    return false;
  }

  const remoteVersion = remote?.version != null ? String(remote.version).trim() : '';
  const bundleUrl = remote?.bundleUrl != null ? String(remote.bundleUrl).trim() : '';
  if (!remoteVersion || !bundleUrl) return false;

  const installed = await getInstalledVersion();
  if (!isRemoteVersionNewer(remoteVersion, installed)) return false;

  let listenerHandle = null;
  try {
    listenerHandle = await CapacitorUpdater.addListener('download', (event) => {
      const pct = Number(event?.percent);
      if (Number.isFinite(pct)) onProgress?.(pct);
    });

    onProgress?.(0);
    const updateData = await CapacitorUpdater.download({
      version: remoteVersion,
      url: bundleUrl,
    });
    await CapacitorUpdater.set(updateData);
    return true;
  } catch (err) {
    console.error('[v2-updater] OTA download/apply failed', err);
    return false;
  } finally {
    try {
      await listenerHandle?.remove();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Run at v2 boot: optional OTA apply, then notifyAppReady when staying on current bundle.
 * @param {(percent: number) => void} [onProgress]
 */
export async function runV2BootUpdates(onProgress) {
  if (!isNative()) return;

  const applied = await checkAndApplyOtaUpdate(onProgress);
  if (!applied) {
    await notifyAppReady();
  }
}
